import { useCallback, useState } from "react";
import type { CreateServiceRequest } from "@/types/api";
import type { Script } from "./UdfFields";
import {
  duplicateScriptFilenames,
  normalizeScriptFilename,
  UdfFields,
} from "./UdfFields";
import { udfScriptPath } from "./udfScriptPath";

const DEFAULT_RHAI_SCRIPT = `fn transform(data, attributes) {
    // Your transformation logic here
    let obj = data;

    // Example: add a processed timestamp
    obj.processed_at = timestamp_ms();

    return result(obj, attributes);
}
`;

export function useUdfFields(serviceName = "") {
  // The Figma redesign (14058-10182) drops the script-path field: the path
  // always derives from the service name via udfScriptPath.
  const scriptPath = udfScriptPath(null, serviceName);
  const [scripts, setScripts] = useState<Script[]>([
    { id: 0, filename: "transform.rhai", content: DEFAULT_RHAI_SCRIPT },
  ]);

  const handleAddScript = useCallback(() => {
    setScripts((prev) => {
      const nextId =
        prev.length === 0 ? 0 : Math.max(...prev.map((s) => s.id)) + 1;
      return [
        ...prev,
        { id: nextId, filename: "", content: DEFAULT_RHAI_SCRIPT },
      ];
    });
  }, []);

  const handleRemoveScript = useCallback((id: number) => {
    setScripts((prev) => prev.filter((s) => s.id !== id));
  }, []);

  const handleFilenameChange = useCallback((id: number, filename: string) => {
    setScripts((prev) =>
      prev.map((s) => (s.id === id ? { ...s, filename } : s)),
    );
  }, []);

  const handleContentChange = useCallback((id: number, content: string) => {
    setScripts((prev) =>
      prev.map((s) => (s.id === id ? { ...s, content } : s)),
    );
  }, []);

  const toPayload = useCallback((): Partial<CreateServiceRequest> => {
    // The server keeps only the last script for a filename, so reject
    // duplicates instead of silently dropping the earlier ones.
    const duplicates = duplicateScriptFilenames(scripts);
    if (duplicates.size > 0) {
      throw new Error(
        `Duplicate script filename: ${[...duplicates].join(", ")}`,
      );
    }

    const validScripts = scripts
      .filter((s) => s.filename.trim() && s.content.trim())
      .map((s) => ({
        filename: normalizeScriptFilename(s.filename),
        content: s.content,
      }));

    if (validScripts.length === 0) {
      throw new Error(
        "At least one script with filename and content is required for UDF services",
      );
    }

    return {
      script_path: scriptPath,
      engine: { kind: "rhai" },
      sources: validScripts,
    };
  }, [scriptPath, scripts]);

  const fields = (
    <UdfFields
      scripts={scripts}
      onAddScript={handleAddScript}
      onRemoveScript={handleRemoveScript}
      onFilenameChange={handleFilenameChange}
      onContentChange={handleContentChange}
    />
  );

  return { fields, toPayload };
}
